import * as Sentry from "@sentry/react";
import { Suspense } from "react";
import { createBrowserRouter, createRoutesFromElements, Navigate, Route } from "react-router-dom";

import { AuthenticatedLayout } from "#app/AuthenticatedLayout";
import { AuthenticatedLayoutV2 } from "#app/AuthenticatedLayoutV2";
import { OnboardingV2Layout } from "#app/OnboardingV2Layout";
import { PublicLayout } from "#app/PublicLayout";
import { loader as AccountLoader, AccountPage } from "#app/routes/pages/Account.page";
import { NoMatch } from "#app/routes/pages/NoMatch.page";
import { SimilarPartPage } from "#app/routes/pages/SimilarPart.page";
import { InformationsSettingsPage } from "#modules/company/view/InformationsSettings.page";
import { QuoteTemplateSettings } from "#modules/company/view/QuoteTemplateSettings";
import { QuotingSettingsPage } from "#modules/company/view/QuotingSettings.page";
const SupportPage = safeLazy(() => import("#app/routes/pages/Support.page"));
import { ClusterPage } from "#app/routes/pages/Cluster.page";
import { CompareClusterPage } from "#app/routes/pages/CompareCluster.page";
import { NotFoundPage } from "#app/routes/pages/NotFound.page";
import { QuoteRequestV2Page } from "#app/routes/pages/quote-requests/QuoteRequestV2.page";
import { loader as QuoteRequestsPageLoader } from "#app/routes/pages/quote-requests/useQuoteRequestData";
import { SearchPage } from "#app/routes/pages/Search.page";
import { safeLazy } from "#shared/helpers/safeLazy";
import { ROUTES } from "#shared/routes";
import { Loader } from "#shared/view/components/Loader";

import {
  ProtectedAppShell,
  ProtectedOnboardingV2Outlet,
  ProtectedV2Outlet,
} from "../../shared/view/components/AppShell/ProtectedAppShell";
import { queryClient } from "../http/ReactQueryProvider";
import { ErrorPage } from "./pages/ErrorPage";
import { LoginPage } from "./pages/LoginPage";
import { ManualSearchPage } from "./pages/ManualSearch.page";
import { OnboardingPage } from "./pages/onboarding/Onboarding.page";
import { PredictionPage } from "./pages/Prediction.page";
import {
  QuoteRequestPage,
  loader as quoteRequestPageLoader,
} from "./pages/quote-requests/QuoteRequest.page";
import { QuoteRequestsPage } from "./pages/quote-requests/QuoteRequests.page";
import { QuoteRequestsPageV2 } from "./pages/quote-requests/QuoteRequestsV2.page";
import { ResetPasswordPage } from "./pages/ResetPassword.page";
import { ResetPasswordRequestPage } from "./pages/ResetPasswordRequest.page";
import { SetCompanyPage } from "./pages/SetCompany.page";
import { SettingsPage } from "./pages/Settings.page";
import { SetUserPage } from "./pages/SetUser.page";
import { SignUpPage } from "./pages/SignUp.page";
import { OnboardingActivityTypeStepPage } from "./pages/v2/onboarding/OnboardingActivityTypeStep.page";
import { OnboardingDefaultProcessStepsStepPage } from "./pages/v2/onboarding/OnboardingDefaultProcessStepsStep.page";
import { OnboardingFinishStepPage } from "./pages/v2/onboarding/OnboardingFinishStep.page";
import { OnboardingMaterialModeStepPage } from "./pages/v2/onboarding/OnboardingMaterialModeStep.page";
import { OnboardingProcessStepConfigurationStepPage } from "./pages/v2/onboarding/OnboardingProcessStepConfigurationStep.page";
import { OnboardingRawMaterialSpecificationStepPage } from "./pages/v2/onboarding/OnboardingRawMaterialSpecificationStep.page";
import { OnboardingVolumetricMaterialSpecificationStepPage } from "./pages/v2/onboarding/OnboardingVolumetricMaterialSpecificationStep.page";
import { OnboardingWelcomePage } from "./pages/v2/onboarding/OnboardingWelcome.page";
import { SearchPageV2 } from "./pages/v2/Search.page";

const ClientsPage = safeLazy(() => import("#modules/clients/view/Clients.page"));
const ClientPage = safeLazy(() => import("#modules/clients/view/Client.page"));
const CompanyMachineViewPage = safeLazy(
  () => import("#modules/machines/view/CompanyMachineView.page"),
);
const IncomingRequestsSettingsPage = safeLazy(
  () => import("#modules/company/view/IncomingRequestsSettings.page"),
);

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

export const router = sentryCreateBrowserRouter(
  createRoutesFromElements(
    <>
      <Route element={<PublicLayout />}>
        <Route path={ROUTES.root} Component={LoginPage} />
        <Route path={ROUTES.login} Component={LoginPage} />
        <Route path={ROUTES.onboarding} Component={OnboardingPage} />
        <Route path={ROUTES.resetPasswordRequest} element={<ResetPasswordRequestPage />} />
        <Route path={ROUTES.resetPassword(":token")} element={<ResetPasswordPage />} />
        <Route path={ROUTES.partnerSignup} element={<SignUpPage />} />
        <Route path={ROUTES.partnerSetUser} element={<SetUserPage />} />
        <Route path={ROUTES.partnerSetCompany} Component={SetCompanyPage} />
        <Route path="*" element={<NoMatch />} />
      </Route>

      <Route element={<AuthenticatedLayout />}>
        <Route path={ROUTES.partner} element={<ProtectedAppShell />} errorElement={<ErrorPage />}>
          <Route
            index
            Component={QuoteRequestsPage}
            loader={QuoteRequestsPageLoader(queryClient)}
          />
          <Route
            path={ROUTES.partnerAccount}
            element={<AccountPage />}
            loader={AccountLoader(queryClient)}
          />
          <Route
            path={ROUTES.partnerQuoteRequests}
            element={<QuoteRequestsPage />}
            loader={QuoteRequestsPageLoader(queryClient)}
          />

          <Route
            path={ROUTES.partnerQuoteRequest(":quoteRequestId")}
            element={<QuoteRequestPage />}
            loader={async ({ params }) => {
              const loaderFn = quoteRequestPageLoader(queryClient, params.quoteRequestId);
              return await loaderFn();
            }}
          />
          <Route
            path={ROUTES.partnerSupport}
            element={
              <Suspense fallback={<Loader />}>
                <SupportPage />
              </Suspense>
            }
          />
          <Route path={ROUTES.partnerSettings} element={<SettingsPage />}>
            <Route
              path={ROUTES.partnerSettingsInformations}
              element={<InformationsSettingsPage />}
            />
            <Route
              path={ROUTES.partnerSettingsAccount}
              element={<AccountPage />}
              loader={AccountLoader(queryClient)}
            />
            <Route
              path={ROUTES.partnerSettingsIncomingRequests}
              element={
                <Suspense fallback={<Loader />}>
                  <IncomingRequestsSettingsPage />
                </Suspense>
              }
            />
            <Route path={ROUTES.partnerSettingsQuoting} element={<QuotingSettingsPage />} />
            <Route
              path={ROUTES.partnerSettingsQuoteTemplates}
              element={<QuoteTemplateSettings />}
            />
            <Route
              path={ROUTES.partnerSettingsMachines}
              element={
                <Suspense fallback={<Loader />}>
                  <CompanyMachineViewPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.partnerSettingsClients}
              element={
                <Suspense fallback={<Loader />}>
                  <ClientsPage />
                </Suspense>
              }
            />
            <Route
              path={ROUTES.partnerSettingsClient(":id")}
              element={
                <Suspense fallback={<Loader />}>
                  <ClientPage />
                </Suspense>
              }
            />
          </Route>
          <Route path={ROUTES.partnerSearch} element={<ManualSearchPage />} />
          <Route path={ROUTES.partnerIa}>
            <Route path={ROUTES.partnerIaSearch} element={<SearchPage />} />
            <Route path={ROUTES.partnerIaPrediction} element={<PredictionPage />} />
            <Route
              path={ROUTES.partnerIaPredictionQuoteRequest(":quoteRequestId")}
              element={<QuoteRequestPage />}
              loader={async ({ params }) => {
                const loaderFn = quoteRequestPageLoader(queryClient, params.quoteRequestId);
                return await loaderFn();
              }}
            />
            <Route path={ROUTES.partnerIaSimilarPart(":partId")} element={<SimilarPartPage />} />
          </Route>

          <Route
            path={ROUTES.partnerQuoteRequestLineCluster(":quoteRequestLineId")}
            element={<ClusterPage />}
          />

          <Route
            path={ROUTES.partnerQuoteRequestLineCompareCluster(":quoteRequestLineId")}
            element={<CompareClusterPage />}
          />
        </Route>
      </Route>

      <Route element={<AuthenticatedLayoutV2 />}>
        <Route path={ROUTES.v2} element={<ProtectedV2Outlet />} errorElement={<ErrorPage />}>
          <Route index element={<Navigate to={ROUTES.v2QuoteRequests} replace />} />
          <Route path={ROUTES.v2QuoteRequests} element={<QuoteRequestsPageV2 />} />
          <Route
            path={ROUTES.v2QuoteRequest(":quoteRequestId")}
            element={<QuoteRequestV2Page />}
            loader={QuoteRequestsPageLoader(queryClient)}
          />
          <Route path={ROUTES.v2Search} element={<SearchPageV2 />} />
          <Route path={"*"} element={<NotFoundPage />} />
        </Route>
      </Route>

      <Route element={<OnboardingV2Layout />}>
        <Route
          path={ROUTES.v2Onboarding}
          element={<ProtectedOnboardingV2Outlet />}
          errorElement={<ErrorPage />}
        >
          <Route index Component={OnboardingWelcomePage} />
          <Route
            path={ROUTES.v2OnboardingActivityType}
            Component={OnboardingActivityTypeStepPage}
          />
          <Route
            path={ROUTES.v2OnboardingMaterialMode}
            Component={OnboardingMaterialModeStepPage}
          />
          <Route
            path={ROUTES.v2OnboardingMaterialSpecification}
            Component={OnboardingVolumetricMaterialSpecificationStepPage}
          />
          <Route
            path={ROUTES.v2OnboardingRawMaterialSpecification}
            Component={OnboardingRawMaterialSpecificationStepPage}
          />
          <Route
            path={ROUTES.v2OnboardingProcessStepConfiguration}
            Component={OnboardingProcessStepConfigurationStepPage}
          />
          <Route
            path={ROUTES.v2OnboardingDefaultProcessSteps}
            Component={OnboardingDefaultProcessStepsStepPage}
          />
          <Route path={ROUTES.v2OnboardingFinish} Component={OnboardingFinishStepPage} />
        </Route>
      </Route>
    </>,
  ),
);
